import React, {
	FC,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useSearchParams } from "react-router-dom";

import { SATOSContentRender } from "@/app/application/SATOSContentRender";
import { Translate } from "@/app/application/Translate";
import { UserContext } from "@/app/providers";
import { useFetchChecklist } from "@/features/procedures/api/checklist.queries";
import {
	ProcedureUpdateSuggestionResponse,
	ProcedureUpdateSuggestionsGroupedByChecklistResponse,
	ProcedureUpdateSuggestionsResponse,
} from "@/features/procedures/model/types/procedureUpdateSuggestions";
import {
	matchesImprovementSubTab,
	type ImprovementsSubTab,
} from "@/features/procedures/lib/procedureUpdateSuggestionVisibility";
import { ManualHeading } from "@/features/procedures/ui/components/checklists/checklist/Checklist";
import { ChecklistRow } from "@/features/procedures/ui/components/checklists/checklist/ChecklistRow";
import {
	ProcedureUpdatesTourSteps,
	getEmbeddedProcedureUpdatesTourSteps,
} from "@/features/task/lib";
import { CustomCard } from "@/features/task/ui/components";
import { StorageKeys } from "@/storage/storageKeys";
import CloseIcon from "@mui/icons-material/Close";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SearchIcon from "@mui/icons-material/Search";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	FormControl,
	Grid,
	IconButton,
	InputAdornment,
	MenuItem,
	Paper,
	Select,
	Skeleton,
	Tab,
	Tabs,
	TextField,
	Typography,
	alpha,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";
import { useTour } from "@reactour/tour";

import {
	useDeleteProcedureUpdateSuggestionComment,
	usePutProcedureUpdateSuggestions,
} from "@/service/ProcedureMutations";
import { useFetchProcedureUpdateSuggestions } from "@/service/TaskQueries";
import { ReadFromLocalStorage, WriteToLocalStorage } from "@/service/Utilities";

/* -------------------------------------------------------------------------- */
/* constants & utils                                                           */
/* -------------------------------------------------------------------------- */

const DOCUMENT_PANEL_ID = "procedure-updates-document-scroll";
const COMMENTS_PANEL_ID = "procedure-updates-comments-scroll";
const CONNECTOR_WIDTH = 28;

const toInt = (v: unknown): number | undefined => {
	const n = Number(v);
	return Number.isFinite(n) ? n : undefined;
};

type ChecklistWithProcedure =
	ProcedureUpdateSuggestionsGroupedByChecklistResponse & {
		ProcedureId: number;
		ProcedureUpdateSuggestions: ProcedureUpdateSuggestionResponse[];
	};

type SuggestionTreeItem = {
	checklist: ChecklistWithProcedure;
	suggestion: ProcedureUpdateSuggestionResponse;
};

type StatusUpdateDetails = {
	procedureId: number;
	stepId: number;
	description: string;
	anonymous: boolean;
	attachment?: unknown;
	declinedComment?: string;
	checklistId?: number | null;
};

const getChecklistName = (
	checklist: ProcedureUpdateSuggestionsGroupedByChecklistResponse,
) =>
	checklist.ChecklistName ||
	checklist.Name ||
	`Checklist ${checklist.ChecklistId}`;

const inferSubTabFromSuggestion = (
	suggestion: ProcedureUpdateSuggestionResponse,
	userId?: number,
): ImprovementsSubTab => {
	const st = String(suggestion.Status ?? "").toLowerCase();
	if (st === "verified" || st === "published") return "verified";
	if (st === "declined") return "declined";
	if (st === "closed") return "closed";
	if ((userId ?? 0) > 0 && (suggestion.CreatedBy?.Id ?? 0) === userId)
		return "myImprov";
	return "newImprov";
};

const findSuggestionById = (
	data: ProcedureUpdateSuggestionsResponse[],
	id: number,
): {
	suggestion: ProcedureUpdateSuggestionResponse;
	checklistId: number;
} | null => {
	for (const procedure of data ?? []) {
		for (const checklist of procedure.Checklists ?? []) {
			const found = (checklist.ProcedureUpdateSuggestions ?? []).find(
				(suggestion) => suggestion.Id === id,
			);
			if (found) {
				return { suggestion: found, checklistId: checklist.ChecklistId };
			}
		}
	}
	return null;
};

function filterSuggestions(
	data: ProcedureUpdateSuggestionsResponse[],
	page: string,
	userId?: number,
): ProcedureUpdateSuggestionsResponse[] {
	if (!Array.isArray(data)) return [];
	return data
		.map((procedure) => ({
			...procedure,
			Checklists: (procedure.Checklists ?? [])
				.map((checklist) => ({
					...checklist,
					ProcedureUpdateSuggestions: (
						checklist.ProcedureUpdateSuggestions ?? []
					).filter((suggestion) => {
						return matchesImprovementSubTab(
							suggestion,
							page as ImprovementsSubTab,
							userId ?? 0,
						);
					}),
				}))
				.filter(
					(checklist) =>
						(checklist.ProcedureUpdateSuggestions?.length ?? 0) > 0,
				),
		}))
		.filter((p) => (p.Checklists?.length ?? 0) > 0);
}

const stripHtml = (s?: string) =>
	s
		? s
			.replace(/<[^>]*>/g, " ")
			.replace(/\s+/g, " ")
			.trim()
		: "";

const truncate = (s: string, n = 80) =>
	s.length > n ? `${s.slice(0, n - 1)}…` : s;

const useHighlight = () => {
	const theme = useTheme();
	const highlightText = useCallback(
		(text?: string, term?: string): React.ReactNode => {
			const t = text ?? "";
			const q = (term ?? "").trim().toLowerCase();
			if (!q) return t;
			const idx = t.toLowerCase().indexOf(q);
			if (idx === -1) return t;
			const before = t.slice(0, idx);
			const match = t.slice(idx, idx + q.length);
			const after = t.slice(idx + q.length);
			return (
				<>
					{before}
					<Box
						component="mark"
						sx={{
							px: 0.25,
							borderRadius: 0.5,
							backgroundColor: alpha(theme.palette.warning.main, 0.25),
						}}
					>
						{match}
					</Box>
					{after}
				</>
			);
		},
		[theme],
	);
	return highlightText;
};

const LabelWithHighlight: React.FC<{
	prefix: string;
	text?: string;
	render: (t?: string) => React.ReactNode;
}> = ({ prefix, text, render }) => (
	<Box
		sx={{
			display: "inline-grid",
			gridTemplateColumns: "auto 1fr",
			columnGap: 1,
			alignItems: "baseline",
			minWidth: 0,
			maxWidth: "100%",
		}}
	>
		<Typography
			component="span"
			variant="caption"
			sx={{ fontWeight: 600, color: "text.secondary" }}
		>
			{prefix}:
		</Typography>
		<Typography
			component="span"
			variant="body2"
			sx={{
				minWidth: 0,
				overflow: "hidden",
				whiteSpace: "nowrap",
				textOverflow: "ellipsis",
			}}
		>
			{render(text)}
		</Typography>
	</Box>
);
LabelWithHighlight.displayName = "LabelWithHighlight";

/* -------------------------------------------------------------------------- */
/* panel paper (aligned with checklist editor / procedure pages)             */
/* -------------------------------------------------------------------------- */

const panelPaperSx = (theme: Theme) => ({
	borderRadius: 3,
	border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === "dark" ? 1 : 0.88)}`,
	boxShadow:
		theme.palette.mode === "dark"
			? "none"
			: theme.shadows[2],
	backgroundColor: theme.palette.background.paper,
	height: "100%",
	display: "flex",
	flexDirection: "column",
	minHeight: 0,
	overflow: "hidden",
});

/** Thin, subtle scrollbar for panels so multiple scrollbars don't look cluttered */
const scrollablePanelSx = (theme: Theme) => ({
	"&::-webkit-scrollbar": { width: 6 },
	"&::-webkit-scrollbar-track": { background: "transparent" },
	"&::-webkit-scrollbar-thumb": {
		borderRadius: 3,
		background: alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.2 : 0.15),
	},
	"&::-webkit-scrollbar-thumb:hover": {
		background: alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.3 : 0.25),
	},
});

const reviewPanelHeaderSx = {
	px: 2,
	py: 1.25,
	borderBottom: 1,
	borderColor: "divider",
	bgcolor: "background.default",
};

/* -------------------------------------------------------------------------- */
/* component                                                                   */
/* -------------------------------------------------------------------------- */

const ProcedureUpdates: FC<{
	page: string;
	hasMaterialHandling?: boolean;
	/** When true, use parent's folder panel, show sub-tabs, filter by folder */
	embeddedInProcedures?: boolean;
	/** When provided, only show procedures in these folders (procedure Ids) */
	procedureIdsInFolder?: Set<number> | null;
	/** When embedded: sync selection from parent folder panel */
	selectedChecklistIdFromParent?: number | null;
	selectedProcedureIdFromParent?: number | null;
	/** When provided with embeddedInProcedures: controlled sub-tab (New/My improvements/Verified/Declined) */
	embeddedSubTab?: ImprovementsSubTab;
	onEmbeddedSubTabChange?: (tab: ImprovementsSubTab) => void;
	/** When embedded on mobile: open parent's folder drawer */
	onOpenFolderDrawer?: () => void;
}> = ({
	page,
	hasMaterialHandling,
	embeddedInProcedures,
	procedureIdsInFolder,
	selectedChecklistIdFromParent,
	selectedProcedureIdFromParent,
	embeddedSubTab,
	onEmbeddedSubTabChange,
	onOpenFolderDrawer,
}) => {
		const theme = useTheme();
		const isMobile = useMediaQuery("(max-width:767px)");
		const user = useContext(UserContext);
		const highlight = useHighlight();
		const { setSteps, setCurrentStep } = useTour();

		// When embedded: use controlled sub-tab if provided, else internal state
		const [internalEmbeddedPage, setInternalEmbeddedPage] =
			useState<ImprovementsSubTab>(() => {
				if (
					["newImprov", "myImprov", "verified", "declined", "closed"].includes(
						page,
					)
				)
					return page as ImprovementsSubTab;
				return "newImprov";
			});
		const embeddedPage = embeddedSubTab ?? internalEmbeddedPage;
		const setEmbeddedPage = onEmbeddedSubTabChange ?? setInternalEmbeddedPage;
		const activePage = embeddedInProcedures ? embeddedPage : page;

		const [searchParams, setSearchParams] = useSearchParams();
		const rawChecklistId = searchParams.get("checklistId");
		const rawStepId = searchParams.get("stepId");
		const rawSuggestionId = searchParams.get("suggestionId");
		// Treat 0 as invalid (no real step/suggestion has id 0) – normalize to undefined
		const urlChecklistId = (() => {
			const n = toInt(rawChecklistId);
			return n != null && n > 0 ? n : undefined;
		})();
		const urlStepId = (() => {
			const n = toInt(rawStepId);
			return n != null && n > 0 ? n : undefined;
		})();
		const urlSuggestionId = (() => {
			const n = toInt(rawSuggestionId);
			return n != null && n > 0 ? n : undefined;
		})();
		// URL with stepId=0 or suggestionId=0 is invalid → we'll show default state and clean URL
		const urlHasInvalidIds = rawStepId === "0" || rawSuggestionId === "0";

		const suggestionsQuery = useFetchProcedureUpdateSuggestions(
			!!hasMaterialHandling,
		) as {
			data?: ProcedureUpdateSuggestionsResponse[];
			isLoading: boolean;
			error: unknown;
			refetch: () => Promise<{ data?: ProcedureUpdateSuggestionsResponse[] }>;
		};

		const {
			data: suggestionGroups,
			isLoading,
			error,
			refetch: refetchSuggestions,
		} = suggestionsQuery;

		const [filteredData, setFilteredData] = useState<
			ProcedureUpdateSuggestionsResponse[]
		>([]);
		const [searchTerm, setSearchTerm] = useState("");
		const [mobileSearchTerm, setMobileSearchTerm] = useState("");

		const [selectedChecklist, setSelectedChecklist] =
			useState<ChecklistWithProcedure | null>(null);
		const [selectedChecklistId, setSelectedChecklistId] = useState<number | null>(
			null,
		);
		const [selectedSuggestionStepId, setSelectedSuggestionStepId] = useState<
			number | null
		>(null);
		const [selectedSuggestion, setSelectedSuggestion] =
			useState<ProcedureUpdateSuggestionResponse | null>(null);

		const [isInteractiveMode] = useState(true);

		const { data: checklist, isLoading: isLoadingChecklist } = useFetchChecklist(
			Number(selectedChecklistId),
			selectedChecklist?.ProcedureId ?? undefined,
		);

		const putProcedureUpdateSuggestions = usePutProcedureUpdateSuggestions();
		const deleteCommentMutation = useDeleteProcedureUpdateSuggestionComment();

		const index = useMemo(() => {
			const checklistById = new Map<number, ChecklistWithProcedure>();
			const suggestionById = new Map<number, ProcedureUpdateSuggestionResponse>();
			const checklistIdBySuggestionId = new Map<number, number>();

			filteredData.forEach((procedure) => {
				(procedure.Checklists ?? []).forEach((checklist) => {
					const checklistWithProcedure: ChecklistWithProcedure = {
						...checklist,
						ProcedureUpdateSuggestions:
							checklist.ProcedureUpdateSuggestions ?? [],
						ProcedureId: procedure.ProcedureId,
					};
					checklistById.set(checklist.ChecklistId, checklistWithProcedure);
					checklistWithProcedure.ProcedureUpdateSuggestions.forEach(
						(suggestion) => {
							suggestionById.set(suggestion.Id, suggestion);
							checklistIdBySuggestionId.set(
								suggestion.Id,
								checklistWithProcedure.ChecklistId,
							);
						},
					);
				});
			});

			return { checklistById, suggestionById, checklistIdBySuggestionId };
		}, [filteredData]);

		useEffect(() => {
			if (!suggestionGroups) return;
			setFilteredData(filterSuggestions(suggestionGroups, activePage, user?.Id));
		}, [suggestionGroups, activePage, user?.Id]);

		// Note: we intentionally do NOT auto-switch tabs when a urlSuggestionId isn't
		// found in the current tab. All deep links generated by the app include the
		// correct improvementsSubTab, so no correction is needed. Auto-switching was
		// causing infinite loops after approve/decline/close mutations.

		useEffect(() => {
			setCurrentStep(0);
			if (setSteps) {
				setSteps(
					embeddedInProcedures
						? getEmbeddedProcedureUpdatesTourSteps(isMobile)
						: ProcedureUpdatesTourSteps,
				);
			}
		}, [embeddedInProcedures, isMobile, setSteps, setCurrentStep]);

		useEffect(() => {
			setSelectedChecklist(null);
			setSelectedChecklistId(null);
			setSelectedSuggestion(null);
			setSelectedSuggestionStepId(null);
		}, [activePage]);

		useEffect(() => {
			// Invalid URL (e.g. stepId=0 or suggestionId=0) → show default state and clean URL
			if (urlHasInvalidIds) {
				setSelectedChecklist(null);
				setSelectedChecklistId(null);
				setSelectedSuggestion(null);
				setSelectedSuggestionStepId(null);
				setSearchParams(
					(prev) => {
						const params = new URLSearchParams(prev);
						params.delete("checklistId");
						params.delete("stepId");
						params.delete("suggestionId");
						return params;
					},
					{ replace: true },
				);
				WriteToLocalStorage(StorageKeys.suggestionSelection(activePage), {});
				return;
			}

			if (urlChecklistId || urlSuggestionId || urlStepId) {
				// Prefer the checklist that owns the suggestion id (more reliable than URL checklistId in deep links)
				const checklistIdFromSuggestion = urlSuggestionId
					? index.checklistIdBySuggestionId.get(urlSuggestionId)
					: undefined;
				const checklistId = checklistIdFromSuggestion ?? urlChecklistId;

				if (checklistId) {
					const checklist = index.checklistById.get(checklistId);
					if (checklist) {
						setSelectedChecklist(checklist);
						setSelectedChecklistId(checklistId);

						const suggestion =
							(urlSuggestionId && index.suggestionById.get(urlSuggestionId)) ??
							checklist.ProcedureUpdateSuggestions.find(
								(item) => item.StepId === urlStepId,
							);

						if (suggestion) {
							setSelectedSuggestion(suggestion);
							setSelectedSuggestionStepId(suggestion.StepId);
						} else if (urlStepId) {
							setSelectedSuggestionStepId(urlStepId);
						}
						return;
					}
				}
			}

			// In embedded mode the parent folder panel is the source of truth for
			// checklist selection. Don't restore from localStorage here — doing so
			// would show a stale checklist whenever the user switches to the
			// Improvements tab without explicitly picking one.
			if (!embeddedInProcedures) {
				const saved = ReadFromLocalStorage<Record<string, unknown>>(
					StorageKeys.suggestionSelection(activePage),
					{},
				);
				const savedChecklistId = toInt(saved.checklistId);
				const savedStepId = toInt(saved.stepId);
				if (savedChecklistId) {
					const checklist = index.checklistById.get(savedChecklistId);
					if (checklist) {
						setSelectedChecklist(checklist);
						setSelectedChecklistId(savedChecklistId);
						if (savedStepId) {
							setSelectedSuggestionStepId(savedStepId);
							const suggestion = checklist.ProcedureUpdateSuggestions.find(
								(item) => item.StepId === savedStepId,
							);
							setSelectedSuggestion(suggestion || null);
						}
					}
				}
			}
		}, [
			urlHasInvalidIds,
			urlChecklistId,
			urlSuggestionId,
			urlStepId,
			index,
			activePage,
			embeddedInProcedures,
		]);

		const updateUrlSelection = (sel: {
			checklistId?: number | null;
			stepId?: number | null;
			suggestionId?: number | null;
		}) => {
			// Use a functional updater so we always start from the LATEST params
			// rather than a stale searchParams closure.
			setSearchParams(
				(prev) => {
					const params = new URLSearchParams(prev);
					const setOrDelete = (k: string, v?: number | null) => {
						if (v == null || Number.isNaN(v)) params.delete(k);
						else params.set(k, String(v));
					};
					setOrDelete("checklistId", sel.checklistId);
					setOrDelete("stepId", sel.stepId);
					setOrDelete("suggestionId", sel.suggestionId);
					return params;
				},
				{ replace: true },
			);

			// Use explicit sel values when provided (including null to clear); otherwise keep current state
			const stored = {
				checklistId:
					sel.checklistId !== undefined
						? (sel.checklistId ?? undefined)
						: (selectedChecklistId ?? undefined),
				stepId:
					sel.stepId !== undefined
						? (sel.stepId ?? undefined)
						: (selectedSuggestionStepId ?? undefined),
				suggestionId:
					sel.suggestionId !== undefined
						? (sel.suggestionId ?? undefined)
						: (selectedSuggestion?.Id ?? undefined),
			};
			WriteToLocalStorage(StorageKeys.suggestionSelection(activePage), stored);
		};

		// When embedded: sync selection from parent folder panel (checklist click)
		useEffect(() => {
			if (
				!embeddedInProcedures ||
				selectedChecklistIdFromParent == null ||
				selectedProcedureIdFromParent == null
			)
				return;
			const checklist = index.checklistById.get(selectedChecklistIdFromParent);
			if (checklist && checklist.ProcedureId === selectedProcedureIdFromParent) {
				setSelectedChecklist(checklist);
				setSelectedChecklistId(selectedChecklistIdFromParent);
				const firstSuggestion = checklist.ProcedureUpdateSuggestions[0];
				if (firstSuggestion) {
					setSelectedSuggestion(firstSuggestion);
					setSelectedSuggestionStepId(firstSuggestion.StepId);
				} else {
					setSelectedSuggestion(null);
					setSelectedSuggestionStepId(null);
				}
				updateUrlSelection({
					checklistId: selectedChecklistIdFromParent,
					stepId: firstSuggestion?.StepId ?? null,
					suggestionId: firstSuggestion?.Id ?? null,
				});
			}
		}, [
			embeddedInProcedures,
			selectedChecklistIdFromParent,
			selectedProcedureIdFromParent,
			index,
		]);

		const refreshSuggestions = async () => {
			const updated = await refetchSuggestions();
			const filtered = filterSuggestions(
				updated.data ?? [],
				activePage,
				user?.Id,
			);
			setFilteredData(filtered);
			// Do NOT call updateUrlSelection here — it would re-add stale selection
			// params (e.g. a just-closed suggestionId) which triggers the deep-link
			// effect to switch tabs. URL selection is managed by callers.
		};

		// Derive from fresh filteredData so after removing the last suggestion, we see 0 and go to default
		const suggestionsForChecklist = useMemo(() => {
			if (selectedChecklistId == null) return [];
			const checklist = index.checklistById.get(selectedChecklistId);
			return checklist?.ProcedureUpdateSuggestions ?? [];
		}, [selectedChecklistId, index]);

		const suggestionCountByStepId = useMemo(() => {
			const map = new Map<number, number>();
			suggestionsForChecklist.forEach((suggestion) => {
				const stepId = suggestion.StepId;
				map.set(stepId, (map.get(stepId) ?? 0) + 1);
			});
			return map;
		}, [suggestionsForChecklist]);

		// When the selected checklist has no suggestions left (e.g. after declining/removing all), return to default state
		useEffect(() => {
			if (selectedChecklistId != null && suggestionsForChecklist.length === 0) {
				setSelectedChecklist(null);
				setSelectedChecklistId(null);
				setSelectedSuggestion(null);
				setSelectedSuggestionStepId(null);
				// Use a functional updater so we only remove the selection params
				// (checklistId / stepId / suggestionId) without touching
				// improvementsSubTab — avoids stale-closure overwrite conflicts.
				setSearchParams(
					(prev) => {
						const next = new URLSearchParams(prev);
						next.delete("checklistId");
						next.delete("stepId");
						next.delete("suggestionId");
						return next;
					},
					{ replace: true },
				);
				WriteToLocalStorage(StorageKeys.suggestionSelection(activePage), {});
			}
		}, [selectedChecklistId, suggestionsForChecklist.length]);

		const commentsPanelRef = useRef<HTMLDivElement | null>(null);
		const connectorRef = useRef<HTMLDivElement | null>(null);
		const documentPanelRef = useRef<HTMLDivElement | null>(null);
		const isSyncingScrollRef = useRef(false);

		type ConnectorLineSegment = {
			stepCenterY: number;
			commentCenterY: number;
			isSelected: boolean;
		};
		const [connectorLines, setConnectorLines] = useState<ConnectorLineSegment[]>(
			[],
		);
		const [connectorHeight, setConnectorHeight] = useState(400);

		const scrollToComment = (suggestionId: number) => {
			setTimeout(() => {
				const el = document.getElementById(`suggestion-comment-${suggestionId}`);
				const container = document.getElementById(COMMENTS_PANEL_ID);
				if (el && container) {
					const cRect = container.getBoundingClientRect();
					const eRect = el.getBoundingClientRect();
					const deltaTop = eRect.top - cRect.top;
					const offset =
						deltaTop - (container.clientHeight / 2 - eRect.height / 2);
					container.scrollTo({
						top: container.scrollTop + offset,
						behavior: "smooth",
					});
				}
			}, 100);
		};

		/** Place step and comment at a fixed position in the viewport so both stay visible (offset accounts for top bar / nav above panels) */
		const scrollStepAndCommentIntoView = (
			stepId: number,
			suggestionId: number,
		) => {
			setTimeout(() => {
				const stepEl = document.getElementById(String(stepId));
				const commentEl = document.getElementById(
					`suggestion-comment-${suggestionId}`,
				);
				const docPanel = document.getElementById(DOCUMENT_PANEL_ID);
				const commentsPanel = document.getElementById(COMMENTS_PANEL_ID);
				if (!stepEl || !commentEl || !docPanel || !commentsPanel) return;

				const docH = docPanel.clientHeight;
				const commentsH = commentsPanel.clientHeight;
				// Position step ~45% from top so it stays visible below headers (embedded view has tabs/header above)
				const targetOffsetFromTop = Math.min(docH, commentsH) * 0.45;

				const stepTop = stepEl.offsetTop;
				const commentTop = commentEl.offsetTop;

				const docScroll = Math.max(
					0,
					Math.min(stepTop - targetOffsetFromTop, docPanel.scrollHeight - docH),
				);
				const commentsScroll = Math.max(
					0,
					Math.min(
						commentTop - targetOffsetFromTop,
						commentsPanel.scrollHeight - commentsH,
					),
				);

				isSyncingScrollRef.current = true;
				docPanel.scrollTo({ top: docScroll, behavior: "smooth" });
				commentsPanel.scrollTo({ top: commentsScroll, behavior: "smooth" });
				requestAnimationFrame(() => {
					setTimeout(() => {
						isSyncingScrollRef.current = false;
					}, 300);
				});
			}, 100);
		};

		const handleCommentIndicatorClick = (stepId: number) => {
			const first = suggestionsForChecklist.find(
				(suggestion) => suggestion.StepId === stepId,
			);
			if (first) {
				setSelectedSuggestion(first);
				setSelectedSuggestionStepId(stepId);
				updateUrlSelection({
					checklistId: selectedChecklistId,
					stepId,
					suggestionId: first.Id,
				});
				scrollToComment(first.Id);
			}
		};

		// When selection changes, place both step and comment at a fixed position in view (~30% from top) so both stay visible
		useEffect(() => {
			if (
				selectedSuggestionStepId == null ||
				!selectedChecklistId ||
				!checklist?.Steps?.length ||
				!selectedSuggestion
			)
				return;
			scrollStepAndCommentIntoView(
				selectedSuggestionStepId,
				selectedSuggestion.Id,
			);
		}, [
			selectedSuggestionStepId,
			selectedChecklistId,
			checklist?.Steps?.length,
			selectedSuggestion?.Id,
		]);

		// Compute connector line positions (step ↔ comments) and update on scroll
		const updateConnectorLines = useCallback(() => {
			if (selectedSuggestionStepId == null || !connectorRef.current) {
				setConnectorLines([]);
				return;
			}
			const connectorEl = connectorRef.current;
			const connectorRect = connectorEl.getBoundingClientRect();
			const stepEl = document.getElementById(String(selectedSuggestionStepId));
			if (!stepEl) {
				setConnectorLines([]);
				return;
			}
			setConnectorHeight(connectorRect.height);
			const stepRect = stepEl.getBoundingClientRect();
			const stepCenterY = stepRect.top + stepRect.height / 2 - connectorRect.top;

			const suggestionsForStep = suggestionsForChecklist.filter(
				(s: { StepId: number }) => s.StepId === selectedSuggestionStepId,
			);
			const segments: ConnectorLineSegment[] = suggestionsForStep.map(
				(s: { Id: number }) => {
					const commentEl = document.getElementById(`suggestion-comment-${s.Id}`);
					const isSelected = selectedSuggestion?.Id === s.Id;
					if (!commentEl) {
						return { stepCenterY, commentCenterY: stepCenterY, isSelected };
					}
					const commentRect = commentEl.getBoundingClientRect();
					const commentCenterY =
						commentRect.top + commentRect.height / 2 - connectorRect.top;
					return { stepCenterY, commentCenterY, isSelected };
				},
			);
			setConnectorLines(segments);
		}, [
			selectedSuggestionStepId,
			selectedSuggestion?.Id,
			suggestionsForChecklist,
		]);

		useEffect(() => {
			updateConnectorLines();
		}, [updateConnectorLines]);

		// Sync scroll: keep step and selected comment at the same level when user scrolls either panel
		const syncScrollToKeepStepAndCommentAligned = useCallback(
			(source: "document" | "comments") => {
				if (!selectedSuggestionStepId || !selectedSuggestion?.Id) return;
				const stepEl = document.getElementById(String(selectedSuggestionStepId));
				const commentEl = document.getElementById(
					`suggestion-comment-${selectedSuggestion.Id}`,
				);
				const docPanel = document.getElementById(DOCUMENT_PANEL_ID);
				const commentsPanel = document.getElementById(COMMENTS_PANEL_ID);
				if (!stepEl || !commentEl || !docPanel || !commentsPanel) return;

				const stepRect = stepEl.getBoundingClientRect();
				const commentRect = commentEl.getBoundingClientRect();
				const docRect = docPanel.getBoundingClientRect();
				const commentsRect = commentsPanel.getBoundingClientRect();

				if (source === "document") {
					const stepOffsetInDocVisible = stepRect.top - docRect.top;
					const commentTop = commentEl.offsetTop;
					const commentHeight = commentEl.getBoundingClientRect().height;
					const targetCommentScroll = commentTop - stepOffsetInDocVisible;
					const maxScroll =
						commentsPanel.scrollHeight - commentsPanel.clientHeight;
					const visibleMin =
						commentTop - (commentsPanel.clientHeight - commentHeight - 24);
					const visibleMax = commentTop;
					const clamped = Math.max(
						visibleMin,
						Math.min(targetCommentScroll, visibleMax),
					);
					const clampedFinal = Math.max(0, Math.min(clamped, maxScroll));
					if (Math.abs(commentsPanel.scrollTop - clampedFinal) > 2) {
						isSyncingScrollRef.current = true;
						commentsPanel.scrollTop = clampedFinal;
						requestAnimationFrame(() => {
							isSyncingScrollRef.current = false;
						});
					}
				} else {
					const commentOffsetInCommentsVisible =
						commentRect.top - commentsRect.top;
					const targetDocScroll =
						stepEl.offsetTop - commentOffsetInCommentsVisible - 8;
					const clamped = Math.max(
						0,
						Math.min(
							targetDocScroll,
							docPanel.scrollHeight - docPanel.clientHeight,
						),
					);
					if (Math.abs(docPanel.scrollTop - clamped) > 2) {
						isSyncingScrollRef.current = true;
						docPanel.scrollTop = clamped;
						requestAnimationFrame(() => {
							isSyncingScrollRef.current = false;
						});
					}
				}
			},
			[selectedSuggestionStepId, selectedSuggestion?.Id],
		);

		useEffect(() => {
			const docPanel = document.getElementById(DOCUMENT_PANEL_ID);
			const commentsPanel = document.getElementById(COMMENTS_PANEL_ID);
			if (!docPanel || !commentsPanel) return;
			const onScroll = (e: Event) => {
				updateConnectorLines();
				if (isSyncingScrollRef.current) return;
				const target = e.target as HTMLElement;
				if (target.id === DOCUMENT_PANEL_ID) {
					syncScrollToKeepStepAndCommentAligned("document");
				} else if (target.id === COMMENTS_PANEL_ID) {
					syncScrollToKeepStepAndCommentAligned("comments");
				}
			};
			const onResize = () => updateConnectorLines();
			docPanel.addEventListener("scroll", onScroll, { passive: true });
			commentsPanel.addEventListener("scroll", onScroll, { passive: true });
			window.addEventListener("resize", onResize);
			return () => {
				docPanel.removeEventListener("scroll", onScroll);
				commentsPanel.removeEventListener("scroll", onScroll);
				window.removeEventListener("resize", onResize);
			};
		}, [updateConnectorLines, syncScrollToKeepStepAndCommentAligned]);

		const handleTreeItemClick = (
			checklistOrSuggestion: ChecklistWithProcedure | SuggestionTreeItem,
			isSuggestion = false,
		) => {
			if (isSuggestion) {
				const { checklist, suggestion } =
					checklistOrSuggestion as SuggestionTreeItem;
				setSelectedChecklist(checklist);
				setSelectedChecklistId(checklist.ChecklistId);
				setSelectedSuggestion(suggestion);
				setSelectedSuggestionStepId(suggestion.StepId);
				updateUrlSelection({
					checklistId: checklist.ChecklistId,
					stepId: suggestion.StepId,
					suggestionId: suggestion.Id,
				});
				scrollToComment(suggestion.Id);
				return;
			}
			const checklist = checklistOrSuggestion as ChecklistWithProcedure;
			setSelectedChecklist(checklist);
			setSelectedChecklistId(checklist.ChecklistId);
			setSelectedSuggestion(null);
			setSelectedSuggestionStepId(null);
			updateUrlSelection({
				checklistId: checklist.ChecklistId,
				stepId: null,
				suggestionId: null,
			});
		};

		const handleStepIdSelected = (stepId: number) => {
			setSelectedSuggestionStepId(stepId);
			const existing = ReadFromLocalStorage<Record<string, unknown>>(
				StorageKeys.suggestionSelection(activePage),
				{},
			);
			WriteToLocalStorage(StorageKeys.suggestionSelection(activePage), {
				...existing,
				stepId,
			});
		};

		const handleUpdateStatusForCard = async (
			suggestionId: number,
			newStatus: string,
			otherDetails: StatusUpdateDetails,
		) => {
			if (!suggestionId || !otherDetails) return;
			const checklistId = otherDetails.checklistId ?? selectedChecklistId ?? null;
			const request = {
				ProcedureId: otherDetails.procedureId,
				ChecklistId: checklistId ?? 0,
				StepId: otherDetails.stepId,
				Description: otherDetails.description,
				Anonymous: otherDetails.anonymous,
				Attachment: otherDetails.attachment || null,
				Status: newStatus,
				DeclinedComment: otherDetails.declinedComment || "",
			};
			try {
				// Clear the suggestion from the URL BEFORE refreshing so that when
				// setFilteredData triggers a re-render the deep-link effect sees
				// urlSuggestionId=null and exits early instead of switching tabs.
				setSearchParams(
					(prev) => {
						const next = new URLSearchParams(prev);
						next.delete("suggestionId");
						next.delete("stepId");
						return next;
					},
					{ replace: true },
				);
				await putProcedureUpdateSuggestions({ id: suggestionId, request });
				await refreshSuggestions();
			} catch (e) {
				console.error("Error updating status", e);
			}
		};

		const handleDeleteComment = async (commentId: number) => {
			try {
				await deleteCommentMutation.mutateAsync(commentId);
				await refreshSuggestions();
			} catch (e) {
				console.error("Failed to delete comment", e);
			}
		};

		const proceduresFiltered = useMemo(() => {
			let list = filteredData ?? [];
			if (procedureIdsInFolder != null) {
				list = list.filter((p) => procedureIdsInFolder.has(p.ProcedureId));
			}
			if (searchTerm.trim()) {
				const q = searchTerm.trim().toLowerCase();
				list = list.filter((p) =>
					String(p.ProcedureName).toLowerCase().includes(q),
				);
			}
			return list;
		}, [filteredData, searchTerm, procedureIdsInFolder]);

		const hasNoData = !isLoading && !error && proceduresFiltered.length === 0;

		// Mobile embedded: flat list of suggestions for simple list view
		const flatSuggestions = useMemo(() => {
			const out: Array<{
				procedure: ProcedureUpdateSuggestionsResponse;
				checklist: ChecklistWithProcedure;
				suggestion: ProcedureUpdateSuggestionResponse;
			}> = [];
			proceduresFiltered.forEach((procedure) => {
				(procedure.Checklists ?? []).forEach((checklist) => {
					(checklist.ProcedureUpdateSuggestions ?? []).forEach((suggestion) => {
						out.push({
							procedure,
							checklist: {
								...checklist,
								ProcedureUpdateSuggestions:
									checklist.ProcedureUpdateSuggestions ?? [],
								ProcedureId: procedure.ProcedureId,
							},
							suggestion,
						});
					});
				});
			});
			return out;
		}, [proceduresFiltered]);

		const checklistOptions = useMemo(() => {
			const seen = new Map<number, string>();
			proceduresFiltered.forEach((procedure) => {
				(procedure.Checklists ?? []).forEach((checklist) => {
					if (checklist.ChecklistId && !seen.has(checklist.ChecklistId)) {
						seen.set(checklist.ChecklistId, getChecklistName(checklist));
					}
				});
			});
			return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
		}, [proceduresFiltered]);

		const [mobileChecklistFilter, setMobileChecklistFilter] = useState<
			number | ""
		>("");
		useEffect(() => {
			setMobileChecklistFilter("");
			setMobileSearchTerm("");
		}, [activePage]);

		const mobileFilteredSuggestions = useMemo(() => {
			let list = flatSuggestions;
			if (mobileChecklistFilter !== "") {
				list = list.filter(
					(x) => x.checklist.ChecklistId === mobileChecklistFilter,
				);
			}
			const q = mobileSearchTerm.trim().toLowerCase();
			if (!q) return list;
			return list.filter(({ procedure, checklist, suggestion }) => {
				const checklistName = getChecklistName(checklist);
				const suggestionText = stripHtml(suggestion.Description);
				return [
					procedure.ProcedureName,
					checklistName,
					suggestionText,
					String(suggestion.StepId ?? ""),
					String(suggestion.Status ?? ""),
				].some((value) => String(value).toLowerCase().includes(q));
			});
		}, [flatSuggestions, mobileChecklistFilter, mobileSearchTerm]);
		const hasMobileFilters =
			mobileChecklistFilter !== "" || !!mobileSearchTerm.trim();

		const showMobileImprovementsView = embeddedInProcedures && isMobile;

		return (
			<Box
				className="procedure-updates-page"
				data-tour="procedure-updates"
				sx={{
					height: "100%",
					minHeight: 0,
					display: "flex",
					flexDirection: "column",
					overflow: "hidden",
					width: "100%",
					backgroundColor: "background.default",
				}}
			>
				{/* Sub-tabs when embedded in Procedures (New, My improvements, Verified, Declined) */}
				{embeddedInProcedures && (
					<Box
						sx={{
							px: { xs: 1.25, sm: 2 },
							pt: 1.5,
							pb: 0,
							flexShrink: 0,
							borderBottom: 1,
							borderColor: "divider",
							bgcolor: "background.default",
						}}
					>
						<Tabs
							data-tour="procedure-updates-subtabs"
							value={embeddedPage}
							onChange={(_, v) => setEmbeddedPage(v as ImprovementsSubTab)}
							aria-label="Improvements view"
							variant="scrollable"
							scrollButtons="auto"
							allowScrollButtonsMobile
							textColor="inherit"
							indicatorColor="primary"
							sx={
								isMobile
									? {
										minHeight: 0,
										py: 0.25,
										"& .MuiTabs-indicator": { display: "none" },
										"& .MuiTabs-flexContainer": { gap: 0.75 },
										"& .MuiTab-root": {
											minHeight: 40,
											py: 1,
											px: 1.5,
											minWidth: "fit-content",
											borderRadius: 2,
											textTransform: "none",
											fontSize: "0.8125rem",
											fontWeight: 700,
											color: "text.secondary",
											border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
											bgcolor: "background.paper",
										},
										"& .MuiTab-root.Mui-selected": {
											color: "primary.main",
											borderColor: alpha(theme.palette.primary.main, 0.35),
											bgcolor: alpha(theme.palette.primary.main, 0.08),
										},
										"& .MuiTabs-scrollButtons": {
											width: 36,
											"&.Mui-disabled": { opacity: 0.2 },
										},
									}
									: {
										minHeight: { xs: 48, sm: 40 },
										borderBottom: `1px solid ${alpha(theme.palette.divider, 0.88)}`,
										"& .MuiTab-root": {
											minHeight: { xs: 48, sm: 40 },
											py: 1,
											px: { xs: 1.5, sm: 2 },
											minWidth: { xs: "auto", sm: 90 },
											textTransform: "none",
											fontSize: { xs: "0.9375rem", sm: "0.875rem" },
											fontWeight: 600,
											color: "text.secondary",
											"&.Mui-selected": {
												color: "text.primary",
												fontWeight: 700,
											},
										},
										"& .MuiTabs-indicator": {
											height: 2,
											backgroundColor: theme.palette.primary.main,
										},
										"& .MuiTabs-scrollButtons": {
											width: { xs: 36, sm: 40 },
											"&.Mui-disabled": { opacity: 0.3 },
										},
									}
							}
						>
							<Tab
								value="newImprov"
								label={
									<Translate String="ProcedureUpdates.Tab.New">New</Translate>
								}
							/>
							<Tab
								value="myImprov"
								label={
									<Translate String="ProcedureUpdates.Tab.MyImprovements">
										My improvements
									</Translate>
								}
							/>
							<Tab
								value="verified"
								label={
									<Translate String="ProcedureUpdates.Tab.Verified">
										Verified
									</Translate>
								}
							/>
							<Tab
								value="declined"
								label={
									<Translate String="ProcedureUpdates.Tab.Declined">
										Declined
									</Translate>
								}
							/>
							<Tab
								value="closed"
								label={
									<Translate String="ProcedureUpdates.Tab.Closed">
										Closed
									</Translate>
								}
							/>
						</Tabs>
					</Box>
				)}

				{/* Mobile embedded: full-featured cards with verify/decline/comment actions */}
				{showMobileImprovementsView ? (
					<Box
						sx={{
							flex: 1,
							minHeight: 0,
							display: "flex",
							flexDirection: "column",
							overflow: "hidden",
							width: "100%",
							maxWidth: 780,
							mx: "auto",
						}}
					>
						{/* Filter bar: Search + Folder + Checklist */}
						<Box
							sx={(t) => ({
								display: "flex",
								flexDirection: "column",
								gap: 1,
								px: 1,
								py: 1,
								flexShrink: 0,
								bgcolor:
									t.palette.mode === "dark"
										? alpha(t.palette.background.paper, 0.6)
										: alpha(t.palette.grey[50], 0.8),
								borderBottom: 1,
								borderColor: "divider",
							})}
						>
							<TextField
								fullWidth
								size="small"
								value={mobileSearchTerm}
								onChange={(e) => setMobileSearchTerm(e.target.value)}
								placeholder="Search suggestions"
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<SearchIcon
												sx={{ fontSize: 18, color: "text.secondary" }}
											/>
										</InputAdornment>
									),
									endAdornment: mobileSearchTerm ? (
										<InputAdornment position="end">
											<IconButton
												size="small"
												aria-label="Clear suggestions search"
												onClick={() => setMobileSearchTerm("")}
												sx={{ borderRadius: 2 }}
											>
												<CloseIcon fontSize="small" />
											</IconButton>
										</InputAdornment>
									) : null,
								}}
								sx={{
									"& .MuiOutlinedInput-root": {
										minHeight: 44,
										borderRadius: 2,
										bgcolor: "background.paper",
									},
								}}
							/>

							<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
								{onOpenFolderDrawer && (
									<Button
										variant="outlined"
										size="small"
										startIcon={<FolderOutlinedIcon sx={{ fontSize: 18 }} />}
										onClick={onOpenFolderDrawer}
										sx={{
											minHeight: 44,
											borderRadius: 2,
											textTransform: "none",
											fontWeight: 700,
											fontSize: "0.875rem",
											flex: "1 1 140px",
										}}
									>
										<Translate String="Procedures.Folders">Folders</Translate>
									</Button>
								)}
								<FormControl
									size="small"
									sx={{ minWidth: 0, flex: "999 1 220px" }}
								>
									<Select
										value={
											mobileChecklistFilter === "" ? "all" : mobileChecklistFilter
										}
										onChange={(e) => {
											const v = e.target.value;
											setMobileChecklistFilter(v === "all" ? "" : Number(v));
										}}
										displayEmpty
										sx={{
											minHeight: 44,
											borderRadius: 2,
											fontSize: "0.875rem",
											bgcolor: "background.paper",
										}}
										renderValue={(v) =>
											v === "all" ? (
												<Translate String="ProcedureUpdates.Filter.AllChecklists">
													All checklists
												</Translate>
											) : (
												(checklistOptions.find((o) => o.id === Number(v))?.name ??
													"")
											)
										}
									>
										<MenuItem value="all">
											<Translate String="ProcedureUpdates.Filter.AllChecklists">
												All checklists
											</Translate>
										</MenuItem>
										{checklistOptions.map((o) => (
											<MenuItem key={o.id} value={o.id} sx={{ py: 1.25 }}>
												{o.name}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Box>

							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
									flexWrap: "wrap",
									gap: 1,
								}}
							>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ fontWeight: 700 }}
								>
									{`${mobileFilteredSuggestions.length} ${mobileFilteredSuggestions.length === 1 ? "suggestion" : "suggestions"}`}
								</Typography>
								{hasMobileFilters && (
									<Button
										variant="text"
										size="small"
										startIcon={<RestartAltIcon sx={{ fontSize: 16 }} />}
										onClick={() => {
											setMobileChecklistFilter("");
											setMobileSearchTerm("");
										}}
										sx={{
											minHeight: 40,
											borderRadius: 2,
											textTransform: "none",
											fontWeight: 700,
										}}
									>
										<Translate String="Procedures.Filter.Reset">
											Reset filters
										</Translate>
									</Button>
								)}
							</Box>
						</Box>

						{/* Scrollable list of suggestion cards with full actions (verify, decline, comment, etc.) */}
						<Box
							sx={{
								flex: 1,
								minHeight: 0,
								overflowY: "auto",
								px: 1,
								py: 1.25,
								"&::-webkit-scrollbar": { width: 6 },
								"&::-webkit-scrollbar-thumb": {
									borderRadius: 3,
									bgcolor: "divider",
								},
							}}
						>
							{isLoading ? (
								<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
									{Array.from({ length: 4 }).map((_, i) => (
										<Skeleton
											key={i}
											variant="rounded"
											height={140}
											sx={{ borderRadius: 2 }}
										/>
									))}
								</Box>
							) : error ? (
								<Alert severity="error" sx={{ borderRadius: 2 }}>
									<Translate String="ProcedureUpdates.ErrorLoad">
										Error loading suggestions
									</Translate>
								</Alert>
							) : mobileFilteredSuggestions.length === 0 ? (
								<Paper
									elevation={0}
									variant="outlined"
									sx={(t) => ({
										py: 5,
										px: 2,
										textAlign: "center",
										borderRadius: 2,
										border: `1px dashed ${alpha(t.palette.divider, 0.8)}`,
										bgcolor:
											t.palette.mode === "dark"
												? alpha(t.palette.background.paper, 0.6)
												: alpha(t.palette.primary.main, 0.04),
									})}
								>
									<Typography variant="body2" color="text.secondary">
										<Translate String="ProcedureUpdates.Empty">
											No procedure suggestions match this filter.
										</Translate>
									</Typography>
								</Paper>
							) : (
								<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
									{mobileFilteredSuggestions.map(
										({ procedure, checklist, suggestion }) => {
											const suggestionWithProcedure: ProcedureUpdateSuggestionResponse =
											{
												...suggestion,
												ProcedureId: procedure.ProcedureId,
											};
											const checklistName = getChecklistName(checklist);
											return (
												<Box
													key={`${procedure.ProcedureId}-${checklist.ChecklistId}-${suggestion.Id}`}
													sx={{
														display: "flex",
														flexDirection: "column",
														gap: 0.75,
													}}
												>
													<Paper
														elevation={0}
														variant="outlined"
														sx={(t) => ({
															px: 1.25,
															py: 1,
															borderRadius: 2,
															borderColor: alpha(t.palette.divider, 0.8),
															bgcolor:
																t.palette.mode === "dark"
																	? alpha(t.palette.background.paper, 0.72)
																	: alpha(t.palette.primary.main, 0.03),
														})}
													>
														<Typography
															variant="subtitle2"
															sx={{
																fontWeight: 800,
																lineHeight: 1.35,
																display: "-webkit-box",
																WebkitLineClamp: 2,
																WebkitBoxOrient: "vertical",
																overflow: "hidden",
															}}
														>
															{procedure.ProcedureName}
														</Typography>
														<Box
															sx={{
																mt: 0.5,
																display: "flex",
																flexWrap: "wrap",
																gap: 0.75,
																alignItems: "center",
															}}
														>
															<Typography
																variant="caption"
																color="text.secondary"
																sx={{ fontWeight: 700 }}
															>
																{checklistName}
															</Typography>
															<Typography
																variant="caption"
																color="text.secondary"
															>
																{`Step ${suggestion.StepId ?? "?"}`}
															</Typography>
														</Box>
													</Paper>
													<CustomCard
														suggestion={suggestionWithProcedure}
														selectedCard={false}
														page={activePage}
														selectedChecklistName={checklistName}
														selectedChecklistProcedureId={procedure.ProcedureId}
														selectedChecklistId={checklist.ChecklistId}
														handleStepIdSelected={() => { }}
														handleUpdateStatusForCard={handleUpdateStatusForCard}
														handleDeleteComment={handleDeleteComment}
														refreshSuggestions={refreshSuggestions}
													/>
												</Box>
											);
										},
									)}
								</Box>
							)}
						</Box>
					</Box>
				) : (
					<Grid
						container
						spacing={2}
						sx={{
							p: { xs: 1, sm: 1 },
							flex: 1,
							minHeight: 0,
							overflow: "hidden",
							width: "100%",
							maxWidth: "100%",
							display: "flex",
							flexDirection: "column",
						}}
					>
						{hasNoData ? (
							<Grid item xs={12}>
								<Paper
									elevation={0}
									variant="outlined"
									sx={(t) => ({
										py: 4,
										px: 2,
										textAlign: "center",
										borderRadius: 2,
										border: `1px dashed ${alpha(t.palette.divider, 0.8)}`,
										backgroundColor:
											t.palette.mode === "dark"
												? alpha(t.palette.background.paper, 0.6)
												: alpha(t.palette.primary.main, 0.04),
										position: "relative",
									})}
								>
									<Typography
										variant="body1"
										color="text.secondary"
										sx={{ mb: 1.5 }}
									>
										<Translate String="ProcedureUpdates.Empty">
											No procedure suggestions match this filter.
										</Translate>
									</Typography>
								</Paper>
							</Grid>
						) : (
							<Grid
								item
								xs={12}
								sx={{
									flex: "1 1 0",
									minHeight: 0,
									display: "flex",
									overflow: "hidden",
								}}
							>
								<Box
									sx={{
										display: "flex",
										flexDirection: "row",
										flex: 1,
										minHeight: 0,
										minWidth: 0,
										width: "100%",
										gap: 0,
										overflow: "hidden",
										alignItems: "stretch",
									}}
								>
									{/* LEFT: Procedures / Checklists tree - hidden when embedded (use parent's folder panel) */}
									{!embeddedInProcedures && (
										<Box
											data-tour="procedure-updates-tree"
											sx={{
												flex: "0 0 280px",
												minHeight: 0,
												overflow: "hidden",
												display: "flex",
											}}
										>
											<Paper
												elevation={0}
												sx={{ ...panelPaperSx(theme), flex: 1, minWidth: 0 }}
											>
												<Box
													sx={(t) => ({
														px: 2,
														pt: 2,
														pb: 1.5,
														borderBottom: 1,
														borderColor: "divider",
														bgcolor:
															t.palette.mode === "dark"
																? "action.hover"
																: "grey.50",
														borderRadius: "12px 12px 0 0",
													})}
												>
													<Typography
														variant="caption"
														sx={{
															display: "block",
															fontWeight: 600,
															color: "text.secondary",
															mb: 1,
														}}
													>
														<Translate String="Procedures.Folders">
															Folders
														</Translate>
													</Typography>
													<TextField
														placeholder="Search procedures…"
														fullWidth
														size="small"
														value={searchTerm}
														onChange={(e) => setSearchTerm(e.target.value)}
														InputProps={{
															startAdornment: (
																<InputAdornment position="start">
																	<SearchIcon
																		fontSize="small"
																		sx={{ color: "text.secondary" }}
																	/>
																</InputAdornment>
															),
														}}
														sx={{
															"& .MuiOutlinedInput-root": {
																borderRadius: 2,
																bgcolor: "background.paper",
																"&.Mui-focused": {
																	"& .MuiOutlinedInput-notchedOutline": {
																		borderColor: (t) =>
																			alpha(t.palette.primary.main, 0.5),
																	},
																},
															},
														}}
													/>
												</Box>
												<Box
													sx={{
														flex: 1,
														minHeight: 0,
														overflowY: "auto",
														px: 1.5,
														py: 1.5,
														...scrollablePanelSx(theme),
													}}
												>
													<SimpleTreeView
														sx={{
															"--TreeView-itemHeight": "44px",
															"& .MuiTreeItem-root": {
																borderRadius: 1.5,
																"&:hover": {
																	backgroundColor: (t) =>
																		alpha(t.palette.primary.main, 0.08),
																},
															},
														}}
													>
														{proceduresFiltered.map((procedure) => (
															<TreeItem
																key={procedure.ProcedureId}
																itemId={String(procedure.ProcedureId)}
																label={
																	<LabelWithHighlight
																		prefix="Procedure"
																		text={procedure.ProcedureName}
																		render={(t) => highlight(t, searchTerm)}
																	/>
																}
															>
																{(procedure.Checklists ?? []).map((checklist) => (
																	<TreeItem
																		key={checklist.ChecklistId}
																		itemId={String(checklist.ChecklistId)}
																		label={
																			<LabelWithHighlight
																				prefix="Checklist"
																				text={getChecklistName(checklist)}
																				render={(t) => highlight(t, searchTerm)}
																			/>
																		}
																		onClick={() =>
																			handleTreeItemClick({
																				...checklist,
																				ProcedureUpdateSuggestions:
																					checklist.ProcedureUpdateSuggestions ??
																					[],
																				ProcedureId: procedure.ProcedureId,
																			})
																		}
																	>
																		{!isInteractiveMode &&
																			(
																				checklist.ProcedureUpdateSuggestions ?? []
																			).map((s) => {
																				const clean = truncate(
																					stripHtml(s.Description) ||
																					`Suggestion #${s.Id}`,
																					90,
																				);
																				return (
																					<TreeItem
																						key={s.Id}
																						itemId={`suggestion-${s.Id}`}
																						label={
																							<LabelWithHighlight
																								prefix="Suggestion"
																								text={clean}
																								render={(t) =>
																									highlight(t, searchTerm)
																								}
																							/>
																						}
																						onClick={() =>
																							handleTreeItemClick(
																								{
																									checklist: {
																										...checklist,
																										ProcedureUpdateSuggestions:
																											checklist.ProcedureUpdateSuggestions ??
																											[],
																										ProcedureId:
																											procedure.ProcedureId,
																									},
																									suggestion: s,
																								},
																								true,
																							)
																						}
																					/>
																				);
																			})}
																	</TreeItem>
																))}
															</TreeItem>
														))}
													</SimpleTreeView>
												</Box>
											</Paper>
										</Box>
									)}

									{/* CENTER: Document (checklist steps) */}
									{selectedChecklist && (
										<Box
											data-tour="procedure-updates-document"
											sx={{
												flex: "1 1 0",
												minWidth: 0,
												minHeight: 0,
												overflow: "hidden",
												display: "flex",
											}}
										>
											<Paper
												elevation={0}
												sx={{ ...panelPaperSx(theme), flex: 1, minWidth: 0 }}
											>
												<Box
													ref={documentPanelRef}
													id={DOCUMENT_PANEL_ID}
													className="procedure-wrapper checklist-view"
													sx={(t) => ({
														flex: 1,
														minHeight: 0,
														overflowY: "auto",
														backgroundColor: t.palette.background.paper,
														...scrollablePanelSx(theme),
														"& .procedure-wrapper-inner": {
															px: { xs: 2, sm: 3 },
															py: 2,
														},
														"& .grid-wrapper": {
															display: "flex",
															flexDirection: "column",
															gap: "4px",
														},
														/* Manual steps: force compact 3-col grid (viewport may be lg+ but panel is narrow) */
														"& .step-row-grid": {
															gridTemplateColumns:
																"40px minmax(0, 1fr) 88px !important",
														},
														"& .step-col-ansvar": { display: "none !important" },
														"& .step-col-comment": { display: "none !important" },
														/* Imported steps: fix col 1 to fixed width, expand content col, hide P&ID/Ansvar */
														"& .grid-column--imported div:nth-child(1)": {
															flex: "0 0 40px !important",
														},
														"& .grid-column--imported div:nth-child(2)": {
															flex: "1 1 0 !important",
														},
														"& .grid-column--imported div:nth-child(3), & .grid-column--imported div:nth-child(4)": {
															display: "none !important",
														},
														/* Imported header row: mirror the same col hiding/sizing */
														"& .grid-column--header p:nth-child(1), & .grid-column--header div:nth-child(1)": {
															flex: "0 0 40px !important",
														},
														"& .grid-column--header p:nth-child(2), & .grid-column--header div:nth-child(2)": {
															flex: "1 1 0 !important",
														},
														"& .grid-column--header p:nth-child(3), & .grid-column--header div:nth-child(3), & .grid-column--header p:nth-child(4), & .grid-column--header div:nth-child(4)": {
															display: "none !important",
														},
														"& .grid-row-header": {
															backgroundColor:
																t.palette.mode === "dark"
																	? t.palette.grey[800]
																	: t.palette.grey[200],
															color: t.palette.text.primary,
														},
														"& .grid-row-item:nth-of-type(odd)": {
															backgroundColor: t.palette.background.paper,
														},
														"& .grid-row-item:nth-of-type(even)": {
															backgroundColor:
																t.palette.mode === "dark"
																	? t.palette.grey[900]
																	: t.palette.grey[50],
														},
													})}
												>
													{isLoadingChecklist ? (
														<Box sx={{ p: 2 }}>
															{Array.from({ length: 5 }).map((_, i) => (
																<Box key={i} sx={{ mb: 2 }}>
																	<Skeleton
																		variant="text"
																		width="90%"
																		height={28}
																	/>
																	<Skeleton
																		variant="text"
																		width="60%"
																		height={24}
																	/>
																</Box>
															))}
														</Box>
													) : (checklist?.Steps?.length ?? 0) > 0 ? (
														<>
															<Box
																sx={{
																	...reviewPanelHeaderSx,
																	px: { xs: 2, sm: 3 },
																	pt: 1.75,
																	pb: 1.75,
																	mb: 0,
																	borderRadius: "12px 12px 0 0",
																}}
															>
																<Box sx={{ display: "flex", flexDirection: "column", gap: 0.35 }}>
																	<Typography
																		variant="overline"
																		sx={{ fontWeight: 800, color: "text.secondary", letterSpacing: "0.06em" }}
																	>
																		Reviewing checklist
																	</Typography>
																	<Typography
																		variant="subtitle1"
																		sx={{ fontWeight: 800, letterSpacing: "-0.01em" }}
																	>
																		{checklist?.Name}
																	</Typography>
																</Box>
																{checklist?.Ingress && (
																	<Box sx={{ mt: 1 }}>
																		<SATOSContentRender
																			isNotProcedureRender
																			content={checklist.Ingress}
																		/>
																	</Box>
																)}
															</Box>
															<Box
																className="procedure-wrapper-inner"
																sx={{ px: { xs: 2, sm: 3 }, py: 2 }}
															>
																<Box className="grid-wrapper">
																	{!checklist?.ImportedChecklist && (
																		<ManualHeading
																			activeSchemeId={selectedChecklistId!}
																		/>
																	)}
																	{checklist?.Steps?.map((step) => (
																		<ChecklistRow
																			key={step.Id}
																			activeSchemeId={selectedChecklistId!}
																			step={step}
																			checklist={checklist}
																			showCommentsAndSuggestion={false}
																			highlightedStepId={
																				selectedSuggestionStepId ?? undefined
																			}
																			scrollContainerId={undefined}
																			suggestionCountByStepId={
																				suggestionCountByStepId
																			}
																			onCommentIndicatorClick={
																				handleCommentIndicatorClick
																			}
																		// highlightLabel={
																		// 	step.Id === selectedSuggestionStepId ? (
																		// 		<Translate String="ProcedureUpdates.LinkedStepLabel">
																		// 			Suggestion refers to this step
																		// 		</Translate>
																		// 	) : undefined
																		// }
																		/>
																	))}
																</Box>
															</Box>
														</>
													) : (
														<Box
															sx={(t) => ({
																py: 4,
																px: 2,
																textAlign: "center",
																borderRadius: 2,
																border: `1px dashed ${alpha(t.palette.divider, 0.8)}`,
																m: 2,
																backgroundColor:
																	t.palette.mode === "dark"
																		? alpha(t.palette.background.paper, 0.6)
																		: alpha(t.palette.primary.main, 0.04),
															})}
														>
															<Typography variant="body2" color="text.secondary">
																<Translate String="ProcedureUpdates.NoSteps">
																	No steps found for this checklist
																</Translate>
															</Typography>
														</Box>
													)}
												</Box>
											</Paper>
										</Box>
									)}

									{/* Connector: lines from step to comment(s) */}
									{selectedChecklist && (
										<Box
											ref={connectorRef}
											sx={(t) => ({
												flex: `0 0 ${CONNECTOR_WIDTH}px`,
												minHeight: 0,
												position: "relative",
												backgroundColor: t.palette.background.default,
												overflow: "hidden",
											})}
										>
											{connectorLines.length > 0 && connectorHeight > 0 && (
												<Box
													component="svg"
													viewBox={`0 0 ${CONNECTOR_WIDTH} ${connectorHeight}`}
													preserveAspectRatio="none"
													sx={{
														position: "absolute",
														top: 0,
														left: 0,
														width: CONNECTOR_WIDTH,
														height: "100%",
														pointerEvents: "none",
													}}
												>
													{connectorLines.map((seg, idx) => {
														const midX = CONNECTOR_WIDTH / 2;
														const stroke = seg.isSelected
															? theme.palette.grey[600]
															: theme.palette.divider;
														const strokeWidth = seg.isSelected ? 2 : 1.25;
														return (
															<path
																key={idx}
																d={`M 0 ${seg.stepCenterY} Q ${midX} ${(seg.stepCenterY + seg.commentCenterY) / 2} ${CONNECTOR_WIDTH} ${seg.commentCenterY}`}
																fill="none"
																stroke={stroke}
																strokeWidth={strokeWidth}
																strokeLinecap="round"
															/>
														);
													})}
												</Box>
											)}
										</Box>
									)}

									{/* RIGHT: Comments panel — fixed width so comments stay readable */}
									<Box
										data-tour="procedure-updates-comments"
										sx={{
											flex: selectedChecklist ? "0 0 420px" : "1 1 0",
											minWidth: selectedChecklist ? 340 : 0,
											flexShrink: 0,
											minHeight: 0,
											overflow: "hidden",
											display: "flex",
										}}
									>
										<Paper
											elevation={0}
											sx={{ ...panelPaperSx(theme), flex: 1, minWidth: 0 }}
										>
											<Box
												sx={{
													display: "flex",
													flexDirection: "column",
													height: "100%",
													minHeight: 0,
												}}
											>
												<Box
													sx={{
														...reviewPanelHeaderSx,
														display: "flex",
														alignItems: "center",
														justifyContent: "space-between",
														flexWrap: "wrap",
														gap: 1,
													}}
												>
													<Box sx={{ display: "flex", flexDirection: "column", gap: 0.35 }}>
														<Typography
															variant="overline"
															sx={{ fontWeight: 800, color: "text.secondary", letterSpacing: "0.06em", lineHeight: 1.1 }}
														>
															Review thread
														</Typography>
													</Box>
													<Box
														sx={{
															display: "flex",
															alignItems: "center",
															gap: 0.75,
														}}
													>
														{selectedChecklist &&
															suggestionsForChecklist.length > 0 && (
																<Box
																	sx={{
																		display: "inline-flex",
																		alignItems: "center",
																		borderRadius: 2,
																		border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
																		bgcolor: "background.paper",
																		overflow: "hidden",
																	}}
																>
																	<IconButton
																		size="small"
																		disabled={
																			!selectedSuggestion ||
																			suggestionsForChecklist.findIndex(
																				(s) => s.Id === selectedSuggestion?.Id,
																			) <= 0
																		}
																		onClick={() => {
																			const idx =
																				suggestionsForChecklist.findIndex(
																					(s) => s.Id === selectedSuggestion?.Id,
																				);
																			if (idx > 0) {
																				const prev =
																					suggestionsForChecklist[idx - 1];
																				setSelectedSuggestion(prev);
																				setSelectedSuggestionStepId(prev.StepId);
																				updateUrlSelection({
																					checklistId: selectedChecklistId,
																					stepId: prev.StepId,
																					suggestionId: prev.Id,
																				});
																				scrollToComment(prev.Id);
																			}
																		}}
																		sx={{
																			borderRadius: 0,
																			px: 1,
																			py: 0.75,
																			color: "text.secondary",
																			"&:not(:disabled)": { color: "primary.main" },
																		}}
																	>
																		<KeyboardArrowLeftIcon sx={{ fontSize: 18 }} />
																	</IconButton>
																	<Box
																		sx={{
																			px: 1,
																			borderLeft: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
																			borderRight: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
																			display: "flex",
																			flexDirection: "column",
																			alignItems: "center",
																		}}
																	>
																		<Typography
																			variant="overline"
																			sx={{ fontSize: "0.6rem", fontWeight: 800, letterSpacing: "0.08em", color: "text.secondary", lineHeight: 1.2 }}
																		>
																			Suggestions
																		</Typography>
																		<Typography
																			variant="body2"
																			sx={{ fontWeight: 800, lineHeight: 1.2, letterSpacing: "-0.01em" }}
																		>
																			{suggestionsForChecklist.findIndex(
																				(s) => s.Id === selectedSuggestion?.Id,
																			) + 1 || 0}{" "}
																			/ {suggestionsForChecklist.length}
																		</Typography>
																	</Box>
																	<IconButton
																		size="small"
																		disabled={
																			!selectedSuggestion ||
																			suggestionsForChecklist.findIndex(
																				(s) => s.Id === selectedSuggestion?.Id,
																			) >=
																			suggestionsForChecklist.length - 1
																		}
																		onClick={() => {
																			const idx =
																				suggestionsForChecklist.findIndex(
																					(s) => s.Id === selectedSuggestion?.Id,
																				);
																			if (
																				idx >= 0 &&
																				idx < suggestionsForChecklist.length - 1
																			) {
																				const next =
																					suggestionsForChecklist[idx + 1];
																				setSelectedSuggestion(next);
																				setSelectedSuggestionStepId(next.StepId);
																				updateUrlSelection({
																					checklistId: selectedChecklistId,
																					stepId: next.StepId,
																					suggestionId: next.Id,
																				});
																				scrollToComment(next.Id);
																			}
																		}}
																		sx={{
																			borderRadius: 0,
																			px: 1,
																			py: 0.75,
																			color: "text.secondary",
																			"&:not(:disabled)": { color: "primary.main" },
																		}}
																	>
																		<KeyboardArrowRightIcon sx={{ fontSize: 18 }} />
																	</IconButton>
																</Box>
															)}
													</Box>
												</Box>
												<Box
													id={COMMENTS_PANEL_ID}
													ref={commentsPanelRef}
													sx={{
														flex: 1,
														minHeight: 0,
														overflowY: "auto",
														px: 2,
														py: 2,
														...scrollablePanelSx(theme),
													}}
												>
													{isLoading ? (
														<Box
															display="flex"
															justifyContent="center"
															alignItems="center"
															minHeight={120}
														>
															<CircularProgress size={32} />
														</Box>
													) : error ? (
														<Alert severity="error">
															<Translate String="ProcedureUpdates.ErrorLoad">
																Error loading suggestions
															</Translate>
														</Alert>
													) : !selectedChecklist ? (
														<Box
															sx={(t) => ({
																py: 3,
																px: 2,
																textAlign: "center",
																borderRadius: 2,
																border: `1px dashed ${alpha(t.palette.divider, 0.8)}`,
																backgroundColor:
																	t.palette.mode === "dark"
																		? alpha(t.palette.background.paper, 0.6)
																		: alpha(t.palette.primary.main, 0.04),
															})}
														>
															<Typography variant="body2" color="text.secondary">
																<Translate String="ProcedureUpdates.SelectChecklist">
																	Select a checklist from the left to view
																	comments
																</Translate>
															</Typography>
														</Box>
													) : suggestionsForChecklist.length === 0 ? (
														<Box
															sx={(t) => ({
																py: 3,
																px: 2,
																textAlign: "center",
																borderRadius: 2,
																border: `1px dashed ${alpha(t.palette.divider, 0.8)}`,
																backgroundColor:
																	t.palette.mode === "dark"
																		? alpha(t.palette.background.paper, 0.6)
																		: alpha(t.palette.primary.main, 0.04),
															})}
														>
															<Typography variant="body2" color="text.secondary">
																<Translate String="ProcedureUpdates.NoComments">
																	No suggestions for this checklist
																</Translate>
															</Typography>
														</Box>
													) : (
														suggestionsForChecklist.map((s) => (
															<Box
																key={s.Id}
																id={`suggestion-comment-${s.Id}`}
																sx={{ mb: 1.5 }}
															>
																<CustomCard
																	selectedChecklistProcedureId={
																		selectedChecklist?.ProcedureId
																	}
																	selectedChecklistName={
																		selectedChecklist?.ChecklistName
																	}
																	selectedChecklistId={selectedChecklistId}
																	suggestion={s}
																	selectedCard={selectedSuggestion?.Id === s.Id}
																	handleStepIdSelected={handleStepIdSelected}
																	handleUpdateStatusForCard={
																		handleUpdateStatusForCard
																	}
																	handleDeleteComment={handleDeleteComment}
																	refreshSuggestions={refreshSuggestions}
																	page={activePage}
																	onOpenSuggestion={(
																		sugg: ProcedureUpdateSuggestionResponse,
																	) => {
																		setSelectedSuggestion(sugg);
																		setSelectedSuggestionStepId(sugg.StepId);
																		updateUrlSelection({
																			checklistId: selectedChecklistId,
																			stepId: sugg.StepId,
																			suggestionId: sugg.Id,
																		});
																		scrollToComment(sugg.Id);
																	}}
																/>
															</Box>
														))
													)}
												</Box>
											</Box>
										</Paper>
									</Box>
								</Box>
							</Grid>
						)}
					</Grid>
				)}
			</Box>
		);
	};

export default ProcedureUpdates;
